import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FaWhatsapp } from 'react-icons/fa';
import { getAllItems } from '../services/api';
import { useCart } from '../context/CartContext';
import CircularLoader from './CircularLoader';

const TrendingItems = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const sectionRef = useRef(null);
  const { addToCart } = useCart();

  const slugify = (text) =>
    (text || '')
      .toString()
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  // Normalize API photo field similar to NewArrivals
  const normalizePhotoValue = (value) => {
    if (!value || typeof value !== 'string') return '';
    const trimmed = value.trim();
    if (trimmed.startsWith('data:image')) return trimmed;
    if (trimmed.startsWith('image/upload/https://') || trimmed.startsWith('image/upload/http://')) {
      return trimmed.replace(/^image\/upload\//, '');
    }
    return trimmed;
  };

  useEffect(() => {
    const currentRef = sectionRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [sectionRef]);

  // Load real products from API and derive trending items
  useEffect(() => {
    const loadTrending = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await getAllItems();
        const items = Array.isArray(response?.items) ? response.items : [];

        const mapped = items
          .map((item) => {
            const rawPrice = parseFloat(item.price);
            const price = Number.isNaN(rawPrice) ? 0 : rawPrice;
            const priceText = `KSh ${price.toLocaleString('en-KE', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}`;

            // Pick a primary image: photo, image, or first from images array with normalization
            let image = '';
            const candidateSingleRaw = item.photo || item.image;
            const candidateSingle = normalizePhotoValue(candidateSingleRaw);
            const candidateArrayRaw = item.images || [];
            const candidateArray = Array.isArray(candidateArrayRaw)
              ? candidateArrayRaw
                  .map((img) => {
                    if (!img) return '';
                    if (typeof img === 'string') return normalizePhotoValue(img);
                    if (typeof img === 'object' && typeof img.image === 'string') return normalizePhotoValue(img.image);
                    return '';
                  })
                  .filter(Boolean)
              : [];

            if (candidateSingle && typeof candidateSingle === 'string' && candidateSingle.trim() !== '') {
              image = candidateSingle;
            } else if (Array.isArray(candidateArray) && candidateArray.length > 0) {
              const thumbObj = Array.isArray(candidateArrayRaw)
                ? candidateArrayRaw.find((x) => x && typeof x === 'object' && x.is_thumbnail)
                : null;
              if (thumbObj && typeof thumbObj.image === 'string') {
                image = normalizePhotoValue(thumbObj.image);
              } else {
                image = candidateArray[0] || '';
              }
            }

            return {
              id: item.id,
              name: item.name,
              price,
              priceText,
              image,
              rating: item.average_rating || 0,
              description: item.description || '',
            };
          })
          .filter((p) => p.id && p.name && p.image);

        // Try to align with admin Trending tab using localStorage IDs.
        let trendingIds = [];
        if (typeof window !== 'undefined') {
          try {
            const raw = window.localStorage.getItem('trendingProductIds');
            const parsed = raw ? JSON.parse(raw) : [];
            if (Array.isArray(parsed)) trendingIds = parsed;
          } catch (e) {
            // ignore parse errors and show no trending items if invalid
          }
        }

        let finalList = [];

        if (trendingIds.length > 0) {
          // Use only items whose IDs are in the admin Trending tab.
          // Match by string to be robust if API returns IDs as strings.
          const idSet = new Set(trendingIds.map((id) => String(id)));
          finalList = mapped.filter((p) => idSet.has(String(p.id)));
        }

        // Fallback: if no admin trending IDs (or none match), use highest-rated items
        if (finalList.length === 0) {
          finalList = [...mapped].sort((a, b) => (b.rating || 0) - (a.rating || 0));
        }

        setProducts(finalList);
      } catch (e) {
        setError(e.message || 'Failed to load trending items');
      } finally {
        setLoading(false);
      }
    };

    loadTrending();
  }, []);

  const nextSlide = () => {
    setCurrentIndex((prev) => {
      if (!products.length) return 0;
      return (prev + 4) % products.length;
    });
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => {
      if (!products.length) return 0;
      return (prev - 4 + products.length) % products.length;
    });
  };

  // Compute up to 4 visible products with wraparound for the carousel
  const visibleProducts = useMemo(() => {
    const list = products;
    const len = list.length;
    if (len === 0) return [];
    if (len <= 4) return list;

    const result = [];
    for (let i = 0; i < 4; i += 1) {
      result.push(list[(currentIndex + i) % len]);
    }
    return result;
  }, [products, currentIndex]);

  const handleAddToCart = (product) => {
    if (!product) return;
    addToCart(product);
  };

  const handleWhatsApp = (product) => {
    if (!product) return;
    const message = `Hello Brand Decor, I would like to inquire about this product: ${product.name}.`;
    const url = `https://wa.me/254714139461?text=${encodeURIComponent(message)}`;
    if (typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const StarRating = ({ rating }) => {
    return (
      <div className="flex items-center space-x-1 mb-3">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            className={`w-5 h-5 ${star <= rating ? 'text-orange-400' : 'text-gray-300'}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    );
  };

  return (
    <div ref={sectionRef} className="py-16 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-6 mb-16">
          <div>
            <h2 className={`text-4xl md:text-5xl font-bold text-gray-900 mb-4 transform transition-all duration-800 ease-out ${
              isVisible ? 'translate-y-0 opacity-100' : '-translate-y-12 opacity-0'
            }`}>
              TRENDING ITEMS
            </h2>
            <p className={`text-lg text-gray-600 transform transition-all duration-800 ease-out delay-200 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}>
              Sitewide Discounts & Savings Of Up To 25%
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="flex items-center gap-3">
              <Link
                to="/shop"
                className="inline-flex items-center justify-center rounded-full px-5 py-3 text-sm font-semibold border border-gray-300 text-gray-800 bg-white hover:bg-gray-50 transition-colors"
              >
                View More
              </Link>
              <Link
                to="/shop"
                className="inline-flex items-center justify-center rounded-full px-5 py-3 text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 transition-colors shadow-sm"
              >
                Shop Here
              </Link>
            </div>

            {/* Navigation Arrows */}
            <div className="flex space-x-3 justify-end">
              <button
                onClick={prevSlide}
                className="p-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-shadow border border-gray-200"
                aria-label="Previous"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={nextSlide}
                className="p-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-shadow border border-gray-200"
                aria-label="Next"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        {error && (
          <p className="text-red-500 text-sm mb-4">{error}</p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {loading && visibleProducts.length === 0 && (
            <div className="col-span-full py-10">
              <CircularLoader label="Loading trending items..." size="lg" />
            </div>
          )}
          {!loading && visibleProducts.length === 0 && !error && (
            <p className="text-gray-500 text-sm col-span-full">No trending items available yet.</p>
          )}
          {visibleProducts.map((product, index) => (
            <div 
              key={product.id}
              className={`bg-gray-50 rounded-3xl p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-2 transform relative group ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-16 opacity-0'
              }`}
              style={{
                transitionDelay: `${400 + index * 100}ms`,
                transitionDuration: '800ms'
              }}
            >
              {/* Product Image */}
              <div className="relative mb-6 overflow-hidden rounded-2xl bg-white p-4">
                <div className="absolute left-4 top-4 z-10">
                  <div className="relative">
                    <span className="inline-flex items-center rounded-full bg-orange-500 text-white text-xs font-extrabold px-3 py-1 shadow-md ring-2 ring-white/60 animate-pulse">
                      Top Deals
                    </span>
                    <span className="absolute -left-1 -top-1 h-2 w-2 rounded-full bg-white/90 animate-ping" />
                  </div>
                </div>
                <img 
                  src={product.image}
                  alt={product.name}
                  className="w-full h-48 object-cover rounded-xl transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                />
              </div>

              {/* Star Rating (display only on card) */}
              <StarRating rating={product.rating} />

              {/* Product Name */}
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {product.name}
              </h3>

              {/* Price */}
              <p className="text-lg font-semibold text-orange-500 mb-4">
                {product.priceText || `KSh ${product.price}`}
              </p>

              {/* Short Description on card */}
              {product.description && (
                <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                  {product.description}
                </p>
              )}

              {/* Actions: Add to Cart + WhatsApp (mirroring NewArrivals style) */}
              <div className="flex flex-col space-y-3 mt-auto">
                <Link
                  to={`/product/${encodeURIComponent(product.id)}/${encodeURIComponent(slugify(product.name))}`}
                  className="w-full bg-white hover:bg-gray-50 text-gray-900 py-3 px-6 rounded-full font-semibold transition-all duration-300 border border-gray-200 hover:shadow-sm touch-manipulation text-center"
                >
                  More Details
                </Link>
                <button
                  onClick={() => handleAddToCart(product)}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 px-6 rounded-full font-semibold transition-all duration-300 hover:scale-105 touch-manipulation"
                >
                  Add To Cart
                </button>

                <button
                  type="button"
                  onClick={() => handleWhatsApp(product)}
                  className="w-full inline-flex items-center justify-start bg-black text-white py-2.5 px-3 rounded-full font-semibold transition-all duration-300 hover:bg-gray-900"
                >
                  <span className="flex items-center justify-center w-9 h-9 rounded-full bg-green-500 text-white mr-2 shadow-sm">
                    <FaWhatsapp className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div className="flex flex-col leading-tight">
                    <span className="text-xs text-green-400">Order On</span>
                    <span className="text-sm font-bold">Whatsapp</span>
                  </div>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TrendingItems;
